import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import moment from 'moment';
import SettingsCard from '../../../../../shared/components/structure/SettingsCard';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import ApiKeyModel from '../../../../../shared/models/auth/api/ApiKeyModel';
import OnboardingProgressModel from '../../../../../shared/models/onboarding/api/OnboardingProgressModel';
import Button from '../../../../../shared/components/general/Button';
import TextField from '../../../../../shared/components/forms/TextField';
import ConfirmationDialog from '../../../../../shared/components/structure/ConfirmationDialog';
import ApiKeyContainer from '../../../../../shared/state/containers/ApiKeyContainer';
import MemberContainer from '../../../../../shared/state/containers/MemberContainer';
import UserContainer from '../../../../../shared/state/containers/UserContainer';
import { generateOnboardingLink, getOnboardingProgressForApplication, updateOnboardingProgressWithApiKey } from '../../../../../shared/api/controllers/onboarding';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from '../../../../../shared/constants/featureFlagIds';
import { getOnboardingBaseUrl } from '../../../../../shared/constants/Urls';
import {
    color_colors_ocean,
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_shades_lightest,
    dark_grey_blue,
    silver_two,
    voicify_spark_lightest
} from '../../../../../shared/constants/colors';
import { getOnboardingFlow, OnboardingFlowConfig, OnboardingFlowType, OnboardingQuestionConfig } from '../../../../onboarding/config/onboardingFlows';
import {
    formatHoursForDisplay,
    formatProcedureTypeAnswerValue,
    isProcedureTypeAnswerValue,
    formatSpecialDatesForDisplay,
    isSpecialDatesAnswerValue,
    ProcedureTypeAnswerValue,
    SpecialDatesAnswerValue,
    WeeklyHoursAnswerValue
} from '../../../../onboarding/utils/onboardingFieldUtils';
import {
    getOnboardingProgressAnswers,
    getOnboardingProgressPhase,
    getTestingStepFeedback,
    getTestingStepFeedbackHistory,
    getTestingProgressState,
    parseOnboardingProgressDetails,
    serializeOnboardingProgressDetails
} from '../../../../onboarding/utils/onboardingProgressDetails';
import { getTestingStepCount as getTestingStepCountForFlow, getTestingSteps } from '../../../../onboarding/utils/onboardingTestingSteps';
import { decodeOnboardingToken } from '../../../../onboarding/utils/decodeOnboardingToken';

interface OnboardingLinksCardProps {
    locationApps?: ApplicationModel[];
    apiKeyContainer: ApiKeyContainer;
    memberContainer: MemberContainer;
    userContainer: UserContainer;
    organizationId?: string;
    isLoading?: boolean;
}

interface LocationApplication extends ApplicationModel {
    id: string;
    name: string;
}

type LinkState = {
    loading?: boolean;
    url?: string;
    copied?: boolean;
    error?: string;
};

type ProgressFieldValue = string | WeeklyHoursAnswerValue | SpecialDatesAnswerValue | ProcedureTypeAnswerValue;

interface ProgressSectionItem {
    key: string;
    label: string;
    value: string;
    isMultiline?: boolean;
    history?: Array<{
        savedAt: string;
        value: string;
    }>;
}

interface ProgressSectionSummary {
    key: string;
    title: string;
    items: ProgressSectionItem[];
}

interface ProgressSummary {
    sectionTitle?: string;
    answerCount: number;
    feedbackCount?: number;
    flowTitle: string;
    responseSections: ProgressSectionSummary[];
    testingFeedbackSections: ProgressSectionSummary[];
}

type ProgressState = {
    loading?: boolean;
    data?: OnboardingProgressModel | null;
    summary?: ProgressSummary | null;
    error?: string;
};

type ProgressDetailsDialogState = {
    application: LocationApplication;
    summary: ProgressSummary;
    progress?: OnboardingProgressModel | null;
};

type TestingFeedbackDialogState = {
    application: LocationApplication;
    summary: ProgressSummary;
    progress?: OnboardingProgressModel | null;
};

const copyIcon = require('../../../../../shared/content/images/copy-text.svg');
const linkIcon = require('../../../../../shared/content/images/ssml/hyperlink-3-blue.svg');
const onboardingApiKeyPrefix = 'Onboarding';
const dayLabels: Array<{ key: keyof WeeklyHoursAnswerValue; label: string }> = [
    { key: 'mondayHours', label: 'Monday' },
    { key: 'tuesdayHours', label: 'Tuesday' },
    { key: 'wednesdayHours', label: 'Wednesday' },
    { key: 'thursdayHours', label: 'Thursday' },
    { key: 'fridayHours', label: 'Friday' },
    { key: 'saturdayHours', label: 'Saturday' },
    { key: 'sundayHours', label: 'Sunday' }
];

const isLocationApplication = (app: ApplicationModel): app is LocationApplication => {
    return typeof app?.id === 'string' && typeof app?.name === 'string';
};

const detectFlowType = (application: ApplicationModel): OnboardingFlowType => {
    const featureFlags = application.featureFlags || [];

    if (featureFlags.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
        return 'food-ordering';
    }

    if (featureFlags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
        return 'healthcare';
    }

    return 'food-ordering';
};

const getProgressAnswers = (progress?: OnboardingProgressModel | null): Record<string, ProgressFieldValue> => {
    return getOnboardingProgressAnswers(progress) as Record<string, ProgressFieldValue>;
};

const isWeeklyHoursAnswer = (value: ProgressFieldValue | undefined): value is WeeklyHoursAnswerValue => {
    return !!value && typeof value === 'object' && 'mondayHours' in value;
};

const humanizeAnswerKey = (value: string): string => value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, match => match.toUpperCase());

const formatAnswerValue = (question: OnboardingQuestionConfig | undefined, value: ProgressFieldValue): { value: string; isMultiline: boolean } => {
    if (isWeeklyHoursAnswer(value)) {
        const formatted = dayLabels
            .map(day => `${day.label}: ${formatHoursForDisplay(value[day.key]) || 'Not provided'}`)
            .join('\n');

        return { value: formatted, isMultiline: true };
    }

    if (isSpecialDatesAnswerValue(value)) {
        const formatted = formatSpecialDatesForDisplay(value);
        return {
            value: formatted,
            isMultiline: formatted.includes('\n')
        };
    }

    if (isProcedureTypeAnswerValue(value)) {
        return {
            value: formatProcedureTypeAnswerValue(value),
            isMultiline: true
        };
    }

    const stringValue = typeof value === 'string' ? value.trim() : '';
    if (!stringValue) {
        return { value: '', isMultiline: false };
    }

    if (question?.allowMultipleChoices) {
        const selectedValues = stringValue.split(',').map(entry => entry.trim()).filter(Boolean);
        const formatted = selectedValues
            .map(selectedValue => question.choices?.find(choice => choice.value === selectedValue)?.label || selectedValue)
            .join(', ');

        return {
            value: formatted,
            isMultiline: false
        };
    }

    const selectedChoice = question?.choices?.find(choice => choice.value === stringValue);
    const displayValue = selectedChoice?.label || stringValue;

    return {
        value: displayValue,
        isMultiline: displayValue.includes('\n')
    };
};

const getCurrentStepTitle = (flow: OnboardingFlowConfig, currentStep?: string): string => {
    if (!currentStep?.trim()) {
        return '';
    }

    const [stepId] = currentStep.split(':');
    return flow.steps.find(step => step.id === stepId)?.title || '';
};

const getCurrentTestingStepTitle = (application: LocationApplication, progress?: OnboardingProgressModel | null): string => {
    const answers = getOnboardingProgressAnswers(progress);
    const flowType = detectFlowType(application);
    const testingState = getTestingProgressState(progress);

    return getTestingSteps(flowType, answers).find(step => step.id === testingState.currentStepId)?.title || '';
};

const buildProgressSummary = (application: LocationApplication, progress?: OnboardingProgressModel | null): ProgressSummary | null => {
    if (!progress) {
        return null;
    }

    const answers = getProgressAnswers(progress);
    const answerEntries = Object.entries(answers).filter(([, value]) => {
        if (isWeeklyHoursAnswer(value as ProgressFieldValue)) {
            return true;
        }

        if (isSpecialDatesAnswerValue(value as ProgressFieldValue)) {
            return (value as SpecialDatesAnswerValue).length > 0;
        }

        if (isProcedureTypeAnswerValue(value as ProgressFieldValue)) {
            return (value as ProcedureTypeAnswerValue).length > 0;
        }

        return typeof value === 'string' && value.trim().length > 0;
    });

    const flow = getOnboardingFlow(detectFlowType(application));
    const questionLookup = flow.steps.reduce((accumulator, step) => {
        step.questions.forEach(question => {
            accumulator[question.id] = question;
        });
        return accumulator;
    }, {} as Record<string, OnboardingQuestionConfig>);

    const sectionMap: Record<string, ProgressSectionSummary> = {};

    answerEntries.forEach(([answerKey, rawValue]) => {
        const question = questionLookup[answerKey];
        const formatted = formatAnswerValue(question, rawValue as ProgressFieldValue);

        if (!formatted.value) {
            return;
        }

        const sectionKey = question?.section || 'Other answers';

        if (!sectionMap[sectionKey]) {
            sectionMap[sectionKey] = {
                key: sectionKey,
                title: sectionKey,
                items: []
            };
        }

        sectionMap[sectionKey].items.push({
            key: answerKey,
            label: question?.title || humanizeAnswerKey(answerKey),
            value: formatted.value,
            isMultiline: formatted.isMultiline
        });
    });

    const orderedSectionTitles = flow.steps
        .reduce((accumulator, step) => {
            if (!accumulator.includes(step.title)) {
                accumulator.push(step.title);
            }

            return accumulator;
        }, [] as string[]);

    const orderedSections = orderedSectionTitles
        .map(title => sectionMap[title])
        .filter(Boolean);

    const remainingSections = Object.values(sectionMap)
        .filter(section => !orderedSections.some(orderedSection => orderedSection.key === section.key));

    const responseSections = orderedSections.concat(remainingSections);
    const testingSteps = getTestingSteps(detectFlowType(application), answers);
    const testingFeedback = getTestingStepFeedback(progress);
    const testingFeedbackHistory = getTestingStepFeedbackHistory(progress);
    const feedbackEntries = testingSteps
        .map(step => ({
            stepId: step.id,
            label: step.title,
            value: typeof testingFeedback[step.id] === 'string' ? testingFeedback[step.id].trim() : '',
            history: (testingFeedbackHistory[step.id] || [])
                .slice()
                .reverse()
                .map(entry => ({
                    savedAt: entry.savedAt,
                    value: entry.value
                }))
        }))
        .filter(entry => entry.history.length > 0);

    const testingFeedbackSections = feedbackEntries.length > 0 ? [{
        key: 'testing-feedback',
        title: 'Testing feedback',
        items: feedbackEntries.map(entry => ({
            key: `testing-feedback-${entry.stepId}`,
            label: entry.label,
            value: entry.value,
            isMultiline: true,
            history: entry.history
        }))
    }] : [];

    return {
        sectionTitle: getOnboardingProgressPhase(progress) === 'testing'
            ? getCurrentTestingStepTitle(application, progress)
            : getCurrentStepTitle(flow, progress.currentStep),
        answerCount: answerEntries.length,
        feedbackCount: feedbackEntries.length,
        flowTitle: flow.title,
        responseSections,
        testingFeedbackSections
    };
};

const getTestingStepCountForApplication = (application: LocationApplication, progress?: OnboardingProgressModel | null): number => {
    const answers = getOnboardingProgressAnswers(progress);
    return getTestingStepCountForFlow(detectFlowType(application), answers);
};

const isTestingProgressComplete = (progress?: OnboardingProgressModel | null): boolean => {
    return !!getTestingProgressState(progress).completedAt;
};

const getDisplayedPercentComplete = (application: LocationApplication, progress?: OnboardingProgressModel | null): number => {
    if (getOnboardingProgressPhase(progress) === 'testing') {
        const testingState = getTestingProgressState(progress);
        return Math.round(((testingState.completedStepIds?.length || 0) / Math.max(getTestingStepCountForApplication(application, progress), 1)) * 100);
    }

    return Math.max(0, Math.min(progress?.percentComplete || 0, 100));
};

const getProgressStatusLabel = (progress?: OnboardingProgressModel | null): string => {
    if (!progress) {
        return 'Not started';
    }

    if (getOnboardingProgressPhase(progress) === 'testing') {
        return isTestingProgressComplete(progress) ? 'Testing complete' : 'Testing mode';
    }

    if (progress.isComplete) {
        return 'Complete';
    }

    if ((progress.percentComplete || 0) > 0) {
        return 'In progress';
    }

    return 'Not started';
};

const buildTestingModeProgressModel = (
    application: LocationApplication,
    progress: OnboardingProgressModel | null | undefined,
    enabled: boolean
): OnboardingProgressModel => {
    const nowIso = new Date().toISOString();
    const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
    const defaultTestingStepId = detectFlowType(application) === 'healthcare' ? 'greeting-office-info' : 'greeting';

    return {
        ...(progress || {}),
        applicationId: application.id,
        currentStep: progress?.currentStep || '',
        percentComplete: progress?.percentComplete || 0,
        isComplete: !!progress?.isComplete,
        lastUpdated: nowIso,
        detailsJson: serializeOnboardingProgressDetails({
            ...existingDetails,
            phase: enabled ? 'testing' : 'onboarding',
            testingEnabledAt: enabled ? (existingDetails.testingEnabledAt || nowIso) : existingDetails.testingEnabledAt,
            onboardingSnapshot: enabled
                ? {
                    currentStep: progress?.currentStep || existingDetails.onboardingSnapshot?.currentStep,
                    percentComplete: progress?.percentComplete || existingDetails.onboardingSnapshot?.percentComplete || 0,
                    isComplete: !!(progress?.isComplete ?? existingDetails.onboardingSnapshot?.isComplete)
                }
                : existingDetails.onboardingSnapshot,
            testing: enabled
                ? {
                    ...existingDetails.testing,
                    currentStepId: existingDetails.testing?.currentStepId || defaultTestingStepId,
                    completedStepIds: existingDetails.testing?.completedStepIds || [],
                    startedAt: existingDetails.testing?.startedAt || nowIso
                }
                : existingDetails.testing
        })
    };
};

const OnboardingLinksCard: React.FC<OnboardingLinksCardProps> = (props) => {
    const [linkStateByAppId, setLinkStateByAppId] = useState<Record<string, LinkState>>({});
    const [progressStateByAppId, setProgressStateByAppId] = useState<Record<string, ProgressState>>({});
    const [globalError, setGlobalError] = useState<string>('');
    const [globalSuccess, setGlobalSuccess] = useState<string>('');
    const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKeyModel>(null);
    const [progressDetailsDialog, setProgressDetailsDialog] = useState<ProgressDetailsDialogState>(null);
    const [testingFeedbackDialog, setTestingFeedbackDialog] = useState<TestingFeedbackDialogState>(null);
    const [deleteError, setDeleteError] = useState<string>('');
    const [appSearch, setAppSearch] = useState<string>('');

    const locationApps = useMemo<LocationApplication[]>(() => {
        return (props.locationApps ?? [])
            .filter(isLocationApplication);
    }, [props.locationApps]);

    const filteredLocationApps = useMemo<LocationApplication[]>(() => {
        const search = appSearch.trim().toLowerCase();

        if (!search) {
            return locationApps;
        }

        return locationApps.filter(application => (application.name || '').toLowerCase().includes(search));
    }, [appSearch, locationApps]);

    const onboardingApiKeys = useMemo<ApiKeyModel[]>(() => {
        return [...(props.apiKeyContainer.state.apiKeys ?? [])]
            .filter(apiKey => (apiKey.name ?? '').startsWith(onboardingApiKeyPrefix))
            .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));
    }, [props.apiKeyContainer.state.apiKeys]);

    useEffect(() => {
        const currentUserId = props.userContainer.state.currentUser?.id;
        if (!currentUserId && props.organizationId) {
            props.userContainer.getUser().then(result => {
                if (result?.resultType === 'Ok' && result.data?.id) {
                    props.memberContainer.checkIsOrgAdmin(props.organizationId, result.data.id);
                }
            });
            return;
        }

        if (props.organizationId && currentUserId) {
            props.memberContainer.checkIsOrgAdmin(props.organizationId, currentUserId);
        }
    }, [props.organizationId, props.userContainer.state.currentUser?.id]);

    const isOrgAdmin = props.memberContainer.state.isOrgAdmin;

    useEffect(() => {
        if (!props.organizationId || !isOrgAdmin) {
            return;
        }

        loadOnboardingApiKeys();
    }, [props.organizationId, isOrgAdmin]);

    useEffect(() => {
        if (props.isLoading) {
            return;
        }

        if (!isOrgAdmin || locationApps.length === 0) {
            setProgressStateByAppId({});
            return;
        }

        loadApplicationProgress();
    }, [isOrgAdmin, locationApps, props.isLoading]);

    const setAppState = (applicationId: string, state: LinkState) => {
        setLinkStateByAppId(current => ({
            ...current,
            [applicationId]: {
                ...current[applicationId],
                ...state
            }
        }));
    };

    const clearFeedback = () => {
        setGlobalError('');
        setGlobalSuccess('');
    };

    const loadOnboardingApiKeys = async (showLoadError: boolean = true) => {
        if (!props.organizationId) {
            return null;
        }

        const result = await props.apiKeyContainer.loadOrganizationApiKeys(props.organizationId);

        if (showLoadError && result.resultType !== 'Ok') {
            setGlobalError(result.errors?.[0] ?? 'Failed to load onboarding links.');
        }

        return result;
    };

    const fetchProgressStateForApplication = async (application: LocationApplication): Promise<ProgressState> => {
        const result = await getOnboardingProgressForApplication(application.id);

        if (result.resultType === 'Ok') {
            return {
                loading: false,
                data: result.data || null,
                summary: buildProgressSummary(application, result.data || null),
                error: ''
            };
        }

        if (result.resultType === 'NotFound') {
            return {
                loading: false,
                data: null,
                summary: null,
                error: ''
            };
        }

        return {
            loading: false,
            data: null,
            summary: null,
            error: result.errors?.[0] || 'Failed to load onboarding progress.'
        };
    };

    const loadApplicationProgressForApp = async (application: LocationApplication): Promise<ProgressState> => {
        setProgressStateByAppId(current => ({
            ...current,
            [application.id]: {
                ...current[application.id],
                loading: true,
                error: ''
            }
        }));

        const nextState = await fetchProgressStateForApplication(application);

        setProgressStateByAppId(current => ({
            ...current,
            [application.id]: {
                ...current[application.id],
                ...nextState
            }
        }));

        return nextState;
    };

    const loadApplicationProgress = async () => {
        const appIds = locationApps.map(app => app.id);

        if (appIds.length === 0) {
            setProgressStateByAppId({});
            return;
        }

        setProgressStateByAppId(current => appIds.reduce((accumulator, applicationId) => ({
            ...accumulator,
            [applicationId]: {
                ...current[applicationId],
                loading: true,
                error: ''
            }
        }), { ...current }));

        const results = await Promise.all(locationApps.map(async application => ({
            applicationId: application.id,
            state: await fetchProgressStateForApplication(application)
        })));

        setProgressStateByAppId(current => results.reduce((accumulator, entry) => ({
            ...accumulator,
            [entry.applicationId]: {
                ...current[entry.applicationId],
                ...entry.state
            }
        }), { ...current }));
    };

    const handleGenerateLink = async (application: LocationApplication) => {
        clearFeedback();
        setAppState(application.id, { loading: true, error: '', copied: false });

        const result = await generateOnboardingLink(application.id);

        if (result.resultType !== 'Ok' || !result.data) {
            const error = result.errors?.[0] ?? 'Failed to generate onboarding link.';
            setAppState(application.id, { loading: false, error });
            setGlobalError(error);
            return;
        }

        const url = `${getOnboardingBaseUrl()}/${result.data}`;

        setAppState(application.id, {
            loading: false,
            url,
            error: '',
            copied: false
        });

        await loadOnboardingApiKeys(false);
        setGlobalSuccess(`Generated onboarding link for ${application.name}.`);
    };

    const handleToggleTestingMode = async (application: LocationApplication, progress?: OnboardingProgressModel | null) => {
        clearFeedback();

        setProgressStateByAppId(current => ({
            ...current,
            [application.id]: {
                ...current[application.id],
                loading: true,
                error: ''
            }
        }));

        const onboardingLinkResult = await generateOnboardingLink(application.id);
        if (onboardingLinkResult.resultType !== 'Ok' || !onboardingLinkResult.data) {
            const error = onboardingLinkResult.errors?.[0] || 'Failed to prepare onboarding testing mode.';
            setProgressStateByAppId(current => ({
                ...current,
                [application.id]: {
                    ...current[application.id],
                    loading: false,
                    error
                }
            }));
            setGlobalError(error);
            return;
        }

        const decodedToken = decodeOnboardingToken(onboardingLinkResult.data);
        setAppState(application.id, {
            url: `${getOnboardingBaseUrl()}/${onboardingLinkResult.data}`,
            error: ''
        });

        const enabled = getOnboardingProgressPhase(progress) !== 'testing';
        const result = await updateOnboardingProgressWithApiKey(buildTestingModeProgressModel(application, progress, enabled), decodedToken.apiKey);

        if (result.resultType !== 'Ok') {
            const error = result.errors?.[0] || 'Failed to update testing mode.';
            setProgressStateByAppId(current => ({
                ...current,
                [application.id]: {
                    ...current[application.id],
                    loading: false,
                    error
                }
            }));
            setGlobalError(error);
            return;
        }

        await loadApplicationProgress();
        setGlobalSuccess(`${enabled ? 'Enabled' : 'Disabled'} testing mode for ${application.name}.`);
    };

    const handleCopyLink = async (application: LocationApplication) => {
        clearFeedback();
        const url = linkStateByAppId[application.id]?.url;

        if (!url) {
            return;
        }

        if (!navigator.clipboard?.writeText) {
            const error = 'Clipboard is not available in this browser.';
            setAppState(application.id, { error });
            setGlobalError(error);
            return;
        }

        try {
            await navigator.clipboard.writeText(url);
            setAppState(application.id, { copied: true, error: '' });
            setGlobalSuccess(`Copied onboarding link for ${application.name}.`);

            setTimeout(() => {
                setLinkStateByAppId(current => {
                    if (!current[application.id]) {
                        return current;
                    }

                    return {
                        ...current,
                        [application.id]: {
                            ...current[application.id],
                            copied: false
                        }
                    };
                });
            }, 2000);
        } catch (_) {
            const error = 'Failed to copy onboarding link.';
            setAppState(application.id, { error });
            setGlobalError(error);
        }
    };

    const openDeleteModal = (apiKey: ApiKeyModel) => {
        setDeleteError('');
        setApiKeyToDelete(apiKey);
    };

    const openProgressDetails = async (application: LocationApplication, summary: ProgressSummary, progress?: OnboardingProgressModel | null) => {
        const latestState = await loadApplicationProgressForApp(application);
        const nextSummary = latestState.summary || summary;
        const nextProgress = latestState.data === undefined ? progress : latestState.data;

        if (!latestState.error && nextSummary) {
            setProgressDetailsDialog({ application, summary: nextSummary, progress: nextProgress });
            return;
        }

        if (summary) {
            setProgressDetailsDialog({ application, summary, progress });
        }
    };

    const closeProgressDetails = () => {
        setProgressDetailsDialog(null);
    };

    const openTestingFeedback = async (application: LocationApplication, summary: ProgressSummary, progress?: OnboardingProgressModel | null) => {
        const latestState = await loadApplicationProgressForApp(application);
        const nextSummary = latestState.summary || summary;
        const nextProgress = latestState.data === undefined ? progress : latestState.data;

        if (!latestState.error && nextSummary) {
            setTestingFeedbackDialog({ application, summary: nextSummary, progress: nextProgress });
            return;
        }

        if (summary) {
            setTestingFeedbackDialog({ application, summary, progress });
        }
    };

    const closeTestingFeedback = () => {
        setTestingFeedbackDialog(null);
    };

    const closeDeleteModal = () => {
        setDeleteError('');
        setApiKeyToDelete(null);
    };

    const handleDeleteLink = async () => {
        if (!apiKeyToDelete?.id) {
            setDeleteError('Unable to delete onboarding link without an API key id.');
            return;
        }

        clearFeedback();
        setDeleteError('');

        const result = await props.apiKeyContainer.deleteApiKey(apiKeyToDelete.id);

        if (result.resultType !== 'Ok') {
            setDeleteError(result.errors?.[0] ?? 'Failed to delete onboarding link.');
            return;
        }

        await loadOnboardingApiKeys(false);
        setGlobalSuccess(`Deleted onboarding link ${apiKeyToDelete.name ?? ''}.`);
        closeDeleteModal();
    };

    const getExpirationLabel = (apiKey: ApiKeyModel) => {
        if (!apiKey.expirationDate) {
            return 'Never';
        }

        return moment(apiKey.expirationDate).format('MM/DD/YYYY');
    };

    const isEmpty = locationApps.length === 0;
    const hasNoSearchResults = !isEmpty && filteredLocationApps.length === 0;

    return (
        <SettingsCard
            title="Onboarding Links"
            subtitle="Generate onboarding URLs for Spark location applications, copy them to share with customers, and revoke active onboarding links when needed."
            icon={linkIcon}
            isLoading={props.isLoading}
        >
            <CardContent>
                {globalError && <Notice type="error">{globalError}</Notice>}
                {globalSuccess && <Notice type="success">{globalSuccess}</Notice>}

                {!isOrgAdmin && (
                    <PermissionNotice>
                        Only organization administrators can generate onboarding links.
                    </PermissionNotice>
                )}

                {!isEmpty && (
                    <SearchRow>
                        <SearchInput
                            type="search"
                            value={appSearch}
                            onChange={(event) => setAppSearch(event.target.value)}
                            placeholder="Search by application name"
                            aria-label="Search onboarding applications"
                        />
                    </SearchRow>
                )}

                {isEmpty ? (
                    <EmptyState>
                        No location applications are available yet. Create at least one location app to generate onboarding links.
                    </EmptyState>
                ) : hasNoSearchResults ? (
                    <SecondaryEmptyState>
                        No onboarding applications match that search.
                    </SecondaryEmptyState>
                ) : (
                    <Rows>
                        {filteredLocationApps.map(application => {
                            const linkState = linkStateByAppId[application.id] ?? {};
                            const progressState = progressStateByAppId[application.id] || ({} as ProgressState);
                            const progress = progressState.data;
                            const progressSummary = progressState.summary;
                            const progressStatusLabel = getProgressStatusLabel(progress);
                            const percentComplete = getDisplayedPercentComplete(application, progress);
                            const isTestingMode = getOnboardingProgressPhase(progress) === 'testing';
                            const isTestingComplete = isTestingProgressComplete(progress);
                            return (
                                <Row key={application.id}>
                                    <RowHeader>
                                        <ApplicationIdentity>
                                            <ApplicationName>{application.name}</ApplicationName>
                                            {isTestingMode && <TestingModeBadge>Testing Mode</TestingModeBadge>}
                                        </ApplicationIdentity>
                                        <RowActions>
                                            <Button
                                                themes={['primary-small']}
                                                text={linkState.url ? 'Regenerate Link' : 'Generate Link'}
                                                type="button"
                                                loading={linkState.loading}
                                                disabled={!isOrgAdmin}
                                                onClick={() => handleGenerateLink(application)}
                                            />
                                            <Button
                                                themes={['secondary-small']}
                                                text={linkState.copied ? 'Copied' : 'Copy'}
                                                type="button"
                                                icon={copyIcon}
                                                disabled={!isOrgAdmin || !linkState.url}
                                                onClick={() => handleCopyLink(application)}
                                            />
                                            <Button
                                                themes={['secondary-small']}
                                                text={progressState.loading ? 'Refreshing...' : 'Refresh Progress'}
                                                type="button"
                                                disabled={!isOrgAdmin || progressState.loading}
                                                onClick={() => loadApplicationProgress()}
                                            />
                                            <Button
                                                themes={['secondary-small']}
                                                text={isTestingMode ? 'Disable Testing' : 'Enable Testing'}
                                                type="button"
                                                disabled={!isOrgAdmin || progressState.loading}
                                                onClick={() => handleToggleTestingMode(application, progress)}
                                            />
                                        </RowActions>
                                    </RowHeader>

                                    <TextField
                                        useForm={false}
                                        name={`onboardingLink-${application.id}`}
                                        label="Onboarding URL"
                                        value={linkState.url ?? ''}
                                        placeholder="Generate a link to display the onboarding URL"
                                        readOnly={true}
                                        fieldContainerStyle={fieldContainerStyle}
                                    />

                                    {linkState.error && (
                                        <InlineError>{linkState.error}</InlineError>
                                    )}

                                    <ProgressPanel>
                                        <ProgressSummaryRow>
                                            <ProgressSummaryCopy>
                                                <ProgressTitle>Onboarding progress</ProgressTitle>
                                                <ProgressMeta>
                                                    <ProgressStatusBadge isComplete={isTestingMode ? isTestingComplete : !!progress?.isComplete}>{progressStatusLabel}</ProgressStatusBadge>
                                                    {isTestingMode && <span>Client testing flow enabled</span>}
                                                    {progressSummary?.flowTitle && <span>{progressSummary.flowTitle}</span>}
                                                    <span>{percentComplete}% complete</span>
                                                    {!!progressSummary?.feedbackCount && <span>{progressSummary.feedbackCount} feedback note{progressSummary.feedbackCount === 1 ? '' : 's'}</span>}
                                                    {progress?.lastUpdated && <span>Updated {moment(progress.lastUpdated).fromNow()}</span>}
                                                    {progressSummary?.sectionTitle && <span>Current section: {progressSummary.sectionTitle}</span>}
                                                </ProgressMeta>
                                            </ProgressSummaryCopy>
                                            <ProgressSummaryActions>
                                                {progressSummary && progressSummary.responseSections.length > 0 && (
                                                    <Button
                                                        themes={['secondary-small']}
                                                        text="Onboarding Form Responses"
                                                        type="button"
                                                        onClick={() => openProgressDetails(application, progressSummary, progress)}
                                                    />
                                                )}
                                                {!!progressSummary?.feedbackCount && (
                                                    <Button
                                                        themes={['secondary-small']}
                                                        text="Testing Feedback"
                                                        type="button"
                                                        onClick={() => openTestingFeedback(application, progressSummary, progress)}
                                                    />
                                                )}
                                            </ProgressSummaryActions>
                                        </ProgressSummaryRow>

                                        {progressState.error && (
                                            <InlineError>{progressState.error}</InlineError>
                                        )}

                                        {!progressState.error && !progressState.loading && !progress && (
                                            <ProgressEmptyState>No onboarding progress has been saved yet.</ProgressEmptyState>
                                        )}

                                        {!progressState.error && (progress || progressState.loading) && (
                                            <>
                                                <ProgressBarTrack>
                                                    <ProgressBarFill percent={percentComplete} />
                                                </ProgressBarTrack>

                                                <ProgressStatGrid>
                                                    <ProgressStatCard>
                                                        <ProgressStatLabel>Saved answers</ProgressStatLabel>
                                                        <ProgressStatValue>{progressSummary?.answerCount || 0}</ProgressStatValue>
                                                    </ProgressStatCard>
                                                    <ProgressStatCard>
                                                        <ProgressStatLabel>Sections touched</ProgressStatLabel>
                                                        <ProgressStatValue>{progressSummary?.responseSections.length || 0}</ProgressStatValue>
                                                    </ProgressStatCard>
                                                </ProgressStatGrid>

                                                {progressState.loading && (
                                                    <ProgressLoadingText>Refreshing saved onboarding progress...</ProgressLoadingText>
                                                )}
                                            </>
                                        )}
                                    </ProgressPanel>
                                </Row>
                            );
                        })}
                    </Rows>
                )}

                {isOrgAdmin && (
                    <ManagedLinksSection>
                        <ManagedLinksHeader>
                            <ManagedLinksTitle>Active Onboarding Links</ManagedLinksTitle>
                            <ManagedLinksDescription>
                                Delete a link to revoke the underlying organization API key and invalidate any shared onboarding URLs that depend on it.
                            </ManagedLinksDescription>
                        </ManagedLinksHeader>

                        {props.apiKeyContainer.state.isLoading && onboardingApiKeys.length === 0 ? (
                            <SecondaryEmptyState>
                                Loading active onboarding links...
                            </SecondaryEmptyState>
                        ) : onboardingApiKeys.length === 0 ? (
                            <SecondaryEmptyState>
                                No active onboarding links have been generated yet.
                            </SecondaryEmptyState>
                        ) : (
                            <ManagedLinksRows>
                                {onboardingApiKeys.map(apiKey => {
                                    const createdByName = apiKey.createdByUserModel?.firstName && apiKey.createdByUserModel?.lastName
                                        ? `${apiKey.createdByUserModel.firstName} ${apiKey.createdByUserModel.lastName}`
                                        : '';

                                    return (
                                        <ManagedLinkRow key={apiKey.id ?? apiKey.name}>
                                            <ManagedLinkInfo>
                                                <ManagedLinkName>{apiKey.name ?? 'Onboarding link'}</ManagedLinkName>
                                                <ManagedLinkMeta>Expires: {getExpirationLabel(apiKey)}</ManagedLinkMeta>
                                                {createdByName && (
                                                    <ManagedLinkMeta>Created by: {createdByName}</ManagedLinkMeta>
                                                )}
                                            </ManagedLinkInfo>
                                            <Button
                                                themes={['secondary-small']}
                                                text="Delete Link"
                                                type="button"
                                                disabled={!apiKey.id || props.apiKeyContainer.state.isLoading}
                                                onClick={() => openDeleteModal(apiKey)}
                                            />
                                        </ManagedLinkRow>
                                    );
                                })}
                            </ManagedLinksRows>
                        )}
                    </ManagedLinksSection>
                )}
            </CardContent>

            {apiKeyToDelete && (
                <ConfirmationDialog
                    title="Delete Onboarding Link"
                    deleteText="Yes, Delete"
                    isLoading={props.apiKeyContainer.state.isLoading}
                    onClose={closeDeleteModal}
                    onConfirm={handleDeleteLink}
                >
                    <DialogContent>
                        <DialogTitle>
                            Delete <b>{apiKeyToDelete.name ?? 'this onboarding link'}</b>?
                        </DialogTitle>
                        <DialogCopy>
                            This removes the underlying organization API key and immediately invalidates any onboarding URLs that rely on it.
                        </DialogCopy>
                        {deleteError && <DialogError>{deleteError}</DialogError>}
                    </DialogContent>
                </ConfirmationDialog>
            )}

            {progressDetailsDialog && (
                <ConfirmationDialog
                    title={`${progressDetailsDialog.application.name} Onboarding Form Responses`}
                    deleteText="Close"
                    warning={true}
                    onClose={closeProgressDetails}
                    onConfirm={closeProgressDetails}
                    size="large"
                    contentScrollable={true}
                    contentClassName={progressDetailsContentClass}
                >
                    <DetailsDialogContent>
                        <DetailsDialogHeader>
                            <DetailsDialogTitle>{progressDetailsDialog.summary.flowTitle}</DetailsDialogTitle>
                            <DetailsDialogMeta>
                                <ProgressStatusBadge isComplete={getOnboardingProgressPhase(progressDetailsDialog.progress) === 'testing' ? isTestingProgressComplete(progressDetailsDialog.progress) : !!progressDetailsDialog.progress?.isComplete}>
                                    {getProgressStatusLabel(progressDetailsDialog.progress)}
                                </ProgressStatusBadge>
                                <span>{getDisplayedPercentComplete(progressDetailsDialog.application, progressDetailsDialog.progress)}% complete</span>
                                {progressDetailsDialog.progress?.lastUpdated && (
                                    <span>Updated {moment(progressDetailsDialog.progress.lastUpdated).fromNow()}</span>
                                )}
                            </DetailsDialogMeta>
                        </DetailsDialogHeader>

                        {progressDetailsDialog.summary.sectionTitle && (
                            <DetailsCurrentSection>Current section: {progressDetailsDialog.summary.sectionTitle}</DetailsCurrentSection>
                        )}

                        <DetailsSections>
                            {progressDetailsDialog.summary.responseSections.map(section => (
                                <DetailsSection key={section.key}>
                                    <DetailsSectionTitle>{section.title}</DetailsSectionTitle>
                                            <DetailsAnswerList>
                                                {section.items.map(item => (
                                                    <DetailsAnswerItem key={item.key}>
                                                        <DetailsAnswerLabel>{item.label}</DetailsAnswerLabel>
                                                        <DetailsAnswerValue isMultiline={!!item.isMultiline}>{item.value}</DetailsAnswerValue>
                                                    </DetailsAnswerItem>
                                                ))}
                                            </DetailsAnswerList>
                                </DetailsSection>
                            ))}
                        </DetailsSections>
                    </DetailsDialogContent>
                </ConfirmationDialog>
            )}

            {testingFeedbackDialog && (
                <ConfirmationDialog
                    title={`${testingFeedbackDialog.application.name} Testing Feedback`}
                    deleteText="Close"
                    warning={true}
                    onClose={closeTestingFeedback}
                    onConfirm={closeTestingFeedback}
                    size="large"
                    contentScrollable={true}
                    contentClassName={progressDetailsContentClass}
                >
                    <DetailsDialogContent>
                        <DetailsDialogHeader>
                            <DetailsDialogTitle>{testingFeedbackDialog.summary.flowTitle}</DetailsDialogTitle>
                            <DetailsDialogMeta>
                                <ProgressStatusBadge isComplete={getOnboardingProgressPhase(testingFeedbackDialog.progress) === 'testing' ? isTestingProgressComplete(testingFeedbackDialog.progress) : !!testingFeedbackDialog.progress?.isComplete}>
                                    {getProgressStatusLabel(testingFeedbackDialog.progress)}
                                </ProgressStatusBadge>
                                <span>{getDisplayedPercentComplete(testingFeedbackDialog.application, testingFeedbackDialog.progress)}% complete</span>
                                {!!testingFeedbackDialog.summary.feedbackCount && <span>{testingFeedbackDialog.summary.feedbackCount} feedback note{testingFeedbackDialog.summary.feedbackCount === 1 ? '' : 's'}</span>}
                                {testingFeedbackDialog.progress?.lastUpdated && (
                                    <span>Updated {moment(testingFeedbackDialog.progress.lastUpdated).fromNow()}</span>
                                )}
                            </DetailsDialogMeta>
                        </DetailsDialogHeader>

                        {testingFeedbackDialog.summary.sectionTitle && (
                            <DetailsCurrentSection>Current testing step: {testingFeedbackDialog.summary.sectionTitle}</DetailsCurrentSection>
                        )}

                        {testingFeedbackDialog.summary.testingFeedbackSections.length === 0 ? (
                            <ProgressEmptyState>No testing feedback has been saved yet.</ProgressEmptyState>
                        ) : (
                            <DetailsSections>
                                {testingFeedbackDialog.summary.testingFeedbackSections.map(section => (
                                    <DetailsSection key={section.key}>
                                        <DetailsSectionTitle>{section.title}</DetailsSectionTitle>
                                        <DetailsAnswerList>
                                            {section.items.map(item => (
                                                <DetailsAnswerItem key={item.key}>
                                                    <DetailsAnswerLabel>{item.label}</DetailsAnswerLabel>
                                                    <DetailsAnswerValue isMultiline={!!item.isMultiline}>{item.value}</DetailsAnswerValue>
                                                    {!!item.history?.length && (
                                                        <DetailsHistoryList>
                                                            {item.history.map((entry, index) => (
                                                                <DetailsHistoryItem key={`${item.key}-history-${index}`}>
                                                                    <DetailsHistoryTimestamp>{moment(entry.savedAt).format('MM/DD/YYYY h:mm A')}</DetailsHistoryTimestamp>
                                                                    <DetailsHistoryValue isMultiline={entry.value.includes('\n')}>{entry.value || 'Cleared'}</DetailsHistoryValue>
                                                                </DetailsHistoryItem>
                                                            ))}
                                                        </DetailsHistoryList>
                                                    )}
                                                </DetailsAnswerItem>
                                            ))}
                                        </DetailsAnswerList>
                                    </DetailsSection>
                                ))}
                            </DetailsSections>
                        )}
                    </DetailsDialogContent>
                </ConfirmationDialog>
            )}
        </SettingsCard>
    );
};

const CardContent = styled.div`
    position: relative;
`;

const Rows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ManagedLinksSection = styled.div`
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid ${silver_two};
`;

const ManagedLinksHeader = styled.div`
    margin-bottom: 16px;
`;

const ManagedLinksTitle = styled.h4`
    margin: 0 0 8px 0;
    color: ${color_shades_darkest};
    font-size: 18px;
    line-height: 24px;
`;

const ManagedLinksDescription = styled.p`
    margin: 0;
    color: ${color_shades_darker};
    font-size: 14px;
    line-height: 20px;
`;

const ManagedLinksRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ManagedLinkRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    border: 1px solid ${silver_two};
    border-radius: 10px;
    background: ${color_shades_lightest};
    padding: 16px 20px;

    @media (max-width: 900px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const ManagedLinkInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ManagedLinkName = styled.p`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 16px;
    line-height: 22px;
    font-weight: 600;
`;

const ManagedLinkMeta = styled.p`
    margin: 0;
    color: ${color_shades_darker};
    font-size: 14px;
    line-height: 20px;
`;

const Row = styled.div`
    border: 1px solid ${silver_two};
    border-radius: 10px;
    background: ${color_shades_lightest};
    padding: 20px;
`;

const RowHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`;

const RowActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;

    button {
        margin: 0;
    }
`;

const ApplicationIdentity = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ApplicationName = styled.h4`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 18px;
    line-height: 24px;
`;

const TestingModeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${voicify_spark_lightest};
    color: ${dark_grey_blue};
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
`;

const SearchRow = styled.div`
    margin-bottom: 16px;
`;

const SearchInput = styled.input`
    width: 100%;
    max-width: 340px;
    min-height: 40px;
    border-radius: 10px;
    border: 1px solid ${silver_two};
    background: ${color_shades_lightest};
    color: ${color_shades_darkest};
    padding: 0 14px;
    font-size: 14px;
    line-height: 20px;

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 2px rgba(0, 125, 187, 0.12);
    }
`;

const PermissionNotice = styled.div`
    border: 1px solid ${color_shades_dark};
    background: ${voicify_spark_lightest};
    color: ${dark_grey_blue};
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
`;

const EmptyState = styled.div`
    border: 1px dashed ${color_shades_dark};
    background: ${color_shades_lightest};
    color: ${color_shades_darker};
    border-radius: 10px;
    padding: 24px;
`;

const SecondaryEmptyState = styled.div`
    border: 1px dashed ${color_shades_dark};
    background: ${color_shades_lightest};
    color: ${color_shades_darker};
    border-radius: 10px;
    padding: 18px 24px;
`;

const InlineError = styled.p`
    margin: -8px 0 0 0;
    color: ${color_colors_ocean};
    font-size: 14px;
`;

const ProgressPanel = styled.div`
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid ${silver_two};
`;

const ProgressSummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`;

const ProgressSummaryCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ProgressSummaryActions = styled.div`
    display: flex;
    align-items: center;

    button {
        margin: 0;
    }
`;

const ProgressTitle = styled.h5`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 16px;
    line-height: 22px;
`;

const ProgressMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: ${color_shades_darker};
    font-size: 13px;
    line-height: 18px;
`;

const ProgressStatusBadge = styled.span<{ isComplete: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    background: ${props => props.isComplete ? '#d4edda' : voicify_spark_lightest};
    color: ${props => props.isComplete ? '#155724' : dark_grey_blue};
    font-weight: 600;
`;

const ProgressBarTrack = styled.div`
    margin-top: 14px;
    width: 100%;
    height: 8px;
    border-radius: 999px;
    background: #dde7f0;
    overflow: hidden;
`;

const ProgressBarFill = styled.div<{ percent: number }>`
    width: ${props => `${props.percent}%`};
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #007dbb 0%, #00a7e1 100%);
`;

const ProgressStatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const ProgressStatCard = styled.div`
    border-radius: 10px;
    border: 1px solid ${silver_two};
    background: #f8fbfe;
    padding: 12px 14px;
`;

const ProgressStatLabel = styled.p`
    margin: 0;
    color: ${color_shades_darker};
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const ProgressStatValue = styled.p`
    margin: 6px 0 0 0;
    color: ${color_shades_darkest};
    font-size: 22px;
    line-height: 28px;
    font-weight: 600;
`;

const progressDetailsContentClass = css`
    display: flex;
    flex-direction: column;
    min-height: 0;
    box-sizing: border-box;
    padding-bottom: 156px !important;
`;

const DetailsDialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px 28px 56px;
    min-height: fit-content;

    @media (max-width: 640px) {
        padding: 20px 20px 24px;
    }
`;

const DetailsDialogHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const DetailsDialogTitle = styled.h4`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 20px;
    line-height: 26px;
`;

const DetailsDialogMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: ${color_shades_darker};
    font-size: 13px;
    line-height: 18px;
`;

const DetailsCurrentSection = styled.p`
    margin: 0;
    color: ${dark_grey_blue};
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
`;

const DetailsSections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const DetailsSection = styled.div`
    border: 1px solid ${silver_two};
    border-radius: 12px;
    background: ${color_shades_lightest};
    padding: 16px 18px;
`;

const DetailsSectionTitle = styled.h5`
    margin: 0 0 12px 0;
    color: ${color_shades_darkest};
    font-size: 16px;
    line-height: 22px;
`;

const DetailsAnswerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DetailsAnswerItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DetailsAnswerLabel = styled.p`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
`;

const DetailsAnswerValue = styled.pre<{ isMultiline: boolean }>`
    margin: 0;
    color: ${color_shades_darker};
    font-size: 13px;
    line-height: 18px;
    font-family: Muli, sans-serif;
    white-space: ${props => props.isMultiline ? 'pre-wrap' : 'normal'};
    word-break: break-word;
`;

const DetailsHistoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed ${silver_two};
`;

const DetailsHistoryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #f8fbfe;
`;

const DetailsHistoryTimestamp = styled.div`
    color: ${color_shades_darker};
    font-size: 11px;
    line-height: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const DetailsHistoryValue = styled.pre<{ isMultiline: boolean }>`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 12px;
    line-height: 17px;
    font-family: Muli, sans-serif;
    white-space: ${props => props.isMultiline ? 'pre-wrap' : 'normal'};
    word-break: break-word;
`;

const ProgressEmptyState = styled.p`
    margin: 14px 0 0 0;
    color: ${color_shades_darker};
    font-size: 13px;
    line-height: 18px;
`;

const ProgressLoadingText = styled.p`
    margin: 10px 0 0 0;
    color: ${color_shades_darker};
    font-size: 13px;
    line-height: 18px;
`;

const Notice = styled.div<{ type: 'error' | 'success' }>`
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.type === 'success' ? '#155724' : dark_grey_blue};
    background: ${props => props.type === 'success' ? '#d4edda' : voicify_spark_lightest};
    border: 1px solid ${props => props.type === 'success' ? '#28a745' : color_shades_dark};
`;

const fieldContainerStyle = css`
    background: ${color_shades_lightest};
    border-color: ${color_shades_dark};
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 40px 32px;
    gap: 16px;
`;

const DialogTitle = styled.p`
    margin: 0;
    color: ${color_shades_darkest};
    font-size: 18px;
    line-height: 24px;
`;

const DialogCopy = styled.p`
    margin: 0;
    color: ${color_shades_darker};
    font-size: 14px;
    line-height: 20px;
`;

const DialogError = styled.p`
    margin: 0;
    color: ${color_colors_ocean};
    font-size: 14px;
    line-height: 20px;
`;

export default OnboardingLinksCard;
